/**
 * InstallTemplateModal.tsx — „Włącz szablon u klienta".
 *
 * Modal wyboru klienta + instalacja gotowego szablonu w jego bazie: wywołuje
 *   POST /admin/module-templates/:slug/install  { tenantId }
 * (backend tworzy tabele encji, wiersze modułów custom, sieje uprawnienia, pisze subskrypcję).
 *
 * UWAGA: instalacja używa wersji szablonu ZAPISANEJ w bazie, nie lokalnych edycji w edytorze.
 * Dlatego przycisk wywołujący ten modal jest blokowany dopóki są niezapisane zmiany (patrz edytor).
 *
 * Obsługa odpowiedzi: 200 (sukces) / 409 ALREADY_INSTALLED / 404 (brak klienta/szablonu)
 *   / 422 (definicja szablonu — pusty / zły slug / nieznany typ pola).
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { AxiosError } from 'axios';

import { Button } from '@/components/Button';
import { Dropdown } from '@/components/Dropdown';
import { FormField } from '@/components/FormField';
import { Modal } from '@/components/Modal';
import { useToast } from '@/contexts/ToastContext';
import { useReservedFieldSlugToast } from '@/hooks/useReservedFieldSlugToast';
import { api } from '@/lib/api';

interface TenantOption {
  id: string;
  code: string;
  name: string;
}

interface InstallResult {
  subscriptionId: string;
  tablesCreated: string[];
  references: string[];
}

function extractErrorCode(err: unknown): string | undefined {
  const ax = err as AxiosError<{ error?: string }>;
  return ax.response?.data?.error;
}

export function InstallTemplateModal({
  open,
  onClose,
  templateSlug,
  templateName,
}: {
  open: boolean;
  onClose: () => void;
  templateSlug: string;
  templateName: string;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const showReservedSlugToast = useReservedFieldSlugToast();
  const [tenantId, setTenantId] = useState('');

  const tenantsQuery = useQuery<{ tenants: TenantOption[] }>({
    queryKey: ['admin', 'tenants', 'for-install'],
    queryFn: async () => (await api.get('/admin/tenants?limit=200')).data,
    enabled: open,
    retry: false,
  });

  useEffect(() => {
    if (open) setTenantId('');
  }, [open]);

  const installMutation = useMutation({
    mutationFn: async (id: string) =>
      (await api.post(`/admin/module-templates/${templateSlug}/install`, { tenantId: id }))
        .data as InstallResult,
    onSuccess: () => {
      toast.show({
        variant: 'success',
        description: t('moduleTemplateEditor.install.toast.success'),
      });
      onClose();
    },
    onError: (err) => {
      // 400 FIELD_SLUG_RESERVED: pole szablonu ma nazwę techniczną zarezerwowaną przez system
      // (np. „decisions") — instalacja cofnięta w całości; komunikat wskazuje pole do zmiany.
      // Tytuł = ogólny błąd instalacji, opis = powód; zostaje do ręcznego zamknięcia (wspólny hak).
      if (
        showReservedSlugToast(err, {
          title: t('moduleTemplateEditor.install.toast.error'),
          variant: 'template',
        })
      ) {
        return;
      }
      const code = extractErrorCode(err);
      const map: Record<string, string> = {
        ALREADY_INSTALLED: t('moduleTemplateEditor.install.toast.alreadyInstalled'),
        NOT_PUBLISHED: t('moduleTemplateEditor.install.toast.notPublished'),
        TENANT_NOT_FOUND: t('moduleTemplateEditor.install.toast.tenantNotFound'),
        TEMPLATE_NOT_FOUND: t('moduleTemplateEditor.install.toast.templateNotFound'),
        EMPTY_TEMPLATE: t('moduleTemplateEditor.install.toast.emptyTemplate'),
        ENTITY_SLUG_TOO_LONG: t('moduleTemplateEditor.install.toast.slugTooLong'),
        TENANT_UPDATING: t('admin.tenants.dbUpdatingError'),
        UNKNOWN_FIELD_TYPE: t('moduleTemplateEditor.install.toast.unknownFieldType'),
      };
      toast.show({
        variant: 'error',
        description: (code && map[code]) || t('moduleTemplateEditor.install.toast.error'),
      });
    },
  });

  const tenantOptions = useMemo(
    () => [
      { value: '', label: t('moduleTemplateEditor.install.pickTenant') },
      ...(tenantsQuery.data?.tenants ?? []).map((tn) => ({
        value: tn.id,
        label: `${tn.name} (${tn.code})`,
      })),
    ],
    [tenantsQuery.data, t],
  );

  const canSubmit = Boolean(tenantId) && !installMutation.isPending;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('moduleTemplateEditor.install.title')}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={() => {
              if (canSubmit) installMutation.mutate(tenantId);
            }}
            disabled={!canSubmit}
          >
            {t('moduleTemplateEditor.install.confirm')}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-3">
        <p className="text-sm text-gray-600 dark:text-gray-300">
          {t('moduleTemplateEditor.install.description', { name: templateName })}
        </p>
        {tenantsQuery.isError ? (
          <p className="text-sm text-red-600 dark:text-red-400">
            {t('moduleTemplateEditor.install.loadTenantsError')}
          </p>
        ) : (
          <FormField label={t('moduleTemplateEditor.install.tenant')}>
            <Dropdown
              options={tenantOptions}
              value={tenantId}
              onChange={(v) => setTenantId(v)}
              disabled={tenantsQuery.isLoading}
            />
          </FormField>
        )}
      </div>
    </Modal>
  );
}
